import * as z from "zod";
import { StorefrontPageAnnouncementSectionDataSchema } from "../primitives/legacy-sections";
import { CartWidgetSchema } from "./cart-widget";

export const CartTopBannerSchema = StorefrontPageAnnouncementSectionDataSchema.pick({
  announcements: true,
  withAnimation: true,
  animationType: true,
  isStickyToTop: true,
  style: true,
}).extend({
  enabled: z.boolean(),
});
export type CartTopBanner = z.infer<typeof CartTopBannerSchema>;

export const CartSellingPlanLineItemConfigSchema = z.object({
  lockItemQuantity: z.boolean().optional(),
  hideSellingPlanSelector: z.boolean().optional(),
  showSavingsAsPercentage: z.boolean().optional(),
  applyConfig: z.enum(["all", "selected"]).optional(),
  selectedProductIds: z.array(z.string()).optional(),
});
export type CartSellingPlanLineItemConfig = z.infer<typeof CartSellingPlanLineItemConfigSchema>;

export const CartSellingPlanUpgradeRuleSchema = z.object({
  fromSellingPlanId: z.string(),
  toSellingPlanId: z.string(),
  applyConfig: z.enum(["all", "selected"]).optional(),
  selectedProductIds: z.array(z.string()).optional(),
  buttonText: z.string().optional(),
});
export type CartSellingPlanUpgradeRule = z.infer<typeof CartSellingPlanUpgradeRuleSchema>;

export const CartSettingsSchema = z.object({
  title: z.string().optional(),
  topBanner: CartTopBannerSchema.optional(),
  sellingPlanLineItemConfig: CartSellingPlanLineItemConfigSchema.optional(),
  sellingPlanUpgradeRules: z.array(CartSellingPlanUpgradeRuleSchema).optional(),
  /** HTML + Handlebars template for custom content below discount code, above price breakdown */
  footerCustomContent: z.string().optional(),
  footerGenericContent: z
    .object({
      jsxAboveCheckoutButton: z.string().optional(),
      jsxBelowCheckoutButton: z.string().optional(),
    })
    .optional(),
  /**
   * Custom JSX widgets rendered below the cart drawer line items. Authored directly in the DB,
   * so an invalid entry drops the widgets instead of failing the whole shop config parse.
   */
  widgets: z.array(CartWidgetSchema).optional().catch(undefined),
  isFreeShippingEnabled: z.boolean().optional(),
  showCartLineItemSellingPlanUpgradeButton: z.boolean().optional(),
  filteredProducts: z
    .array(
      z.object({
        productId: z.string(),
      }),
    )
    .optional(),
  hideDiscountCodeInput: z.boolean().optional(),
  hideShippingCalculation: z.boolean().optional(),
  showPricesOnButtons: z.boolean().optional(),
  disableProductQuickView: z.boolean().optional(),
  upsellImageFit: z
    .enum(["cover", "contain"])
    .optional()
    .describe("Cart upsell image fit. Defaults to cover when omitted."),
  removeZeroDecimals: z.boolean().optional(),
  maxQuantityPerProduct: z.number().int().positive().optional(),
});
export type CartSettings = z.infer<typeof CartSettingsSchema>;
